import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Link as LinkIcon, 
  Copy, 
  Share2, 
  Users, 
  TrendingUp,
  Trophy,
  Coins,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function ReferralsSection() {
  const [stats, setStats] = useState(null);
  const [referralLink, setReferralLink] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      
      const [statsRes, linkRes, referralsRes] = await Promise.all([
        fetch(`${baseUrl}/api/referrals/my-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/api/referrals/my-link`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/api/referrals/my-referrals`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (linkRes.ok) setReferralLink(await linkRes.json());
      if (referralsRes.ok) setReferrals(await referralsRes.json());
    } catch (error) {
      console.error('Failed to load referrals data:', error);
      toast.error('Erro ao carregar dados de indicações');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!referralLink) return;
    
    try {
      await navigator.clipboard.writeText(referralLink.referral_link);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  const shareLink = async () => {
    if (!referralLink) return;
    
    const shareData = {
      title: 'U-Clean - Rede Social de Recuperação',
      text: 'Entre na U-Clean, uma rede social gratuita de apoio à recuperação! Use meu link de convite:',
      url: referralLink.referral_link
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success('Link compartilhado!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyLink(); // Fallback para copiar
        }
      }
    } else {
      copyLink(); // Fallback para copiar
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Link de Convite */}
      <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-blue-600" />
            Meu Link de Convite
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referralLink && (
            <>
              <div className="bg-white rounded-lg p-4 mb-4 border-2 border-blue-300">
                <div className="text-sm text-gray-600 mb-2">Seu código único:</div>
                <div className="font-mono text-lg font-bold text-blue-600 mb-3">
                  {referralLink.referral_code}
                </div>
                <div className="text-sm text-gray-600 mb-2">Link completo:</div>
                <div className="font-mono text-sm bg-gray-50 p-3 rounded break-all">
                  {referralLink.referral_link}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={copyLink}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copiar Link
                    </>
                  )}
                </Button>
                <Button
                  onClick={shareLink}
                  variant="outline"
                  className="flex-1"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-3 text-center">
                💡 Compartilhe este link e ganhe +20 tokens por cada amigo que se cadastrar!
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas */}
      {stats && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {stats.successful_referrals}
              </div>
              <div className="text-sm text-gray-600">Indicações Completas</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Coins className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-yellow-600 mb-1">
                {stats.tokens_earned}
              </div>
              <div className="text-sm text-gray-600">Tokens Ganhos</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Trophy className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {stats.rank ? `#${stats.rank}` : '-'}
              </div>
              <div className="text-sm text-gray-600">Posição no Ranking</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista de Pessoas Indicadas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Pessoas que Você Indicou ({referrals.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Você ainda não indicou ninguém</p>
              <p className="text-sm mt-2">Compartilhe seu link e comece a ganhar tokens!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      referral.status === 'completed' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      {referral.status === 'completed' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <Clock className="h-5 w-5 text-orange-600" />
                      )}
                    </div>
                    
                    <div>
                      <div className="font-semibold">{referral.name}</div>
                      <div className="text-sm text-gray-500">
                        {formatDistanceToNow(new Date(referral.referred_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    {referral.status === 'completed' ? (
                      <>
                        <Badge className="bg-green-100 text-green-800 border-green-300">
                          Ativo
                        </Badge>
                        <div className="text-sm text-yellow-600 font-semibold mt-1">
                          +{referral.tokens_awarded} tokens
                        </div>
                      </>
                    ) : (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                        Pendente
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
